import * as React from 'react';

import usePageData from 'bundles/enroll-modal/data/usePageData';
import { getS12nDisplayName } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/enroll';

/**
 * Returns the PMM-approved specialization context copy for the course enrollment modal, shown
 * when the mix-and-match selection modal is skipped (learner flow: S12N page -> course page ->
 * enrollment modal), e.g.
 * "Introduction to Cybersecurity Tools & Cyberattacks is part of the IBM Cybersecurity Analyst
 *  Professional Certificate and is Course 3 of 14."
 *
 * Returns null if the specialization is unavailable or the course is not part of it. Exposed as a
 * hook so callers that need a plain string (e.g. CDS `supportText`) can use it directly.
 */
export const useMixAndMatchS12nContextCopy = (variant: 'default' | 'experiment' = 'default'): string | null => {
  const { course, s12n } = usePageData();

  if (!s12n) {
    return null;
  }

  const coursePosition = s12n.courseIds.indexOf(course.id) + 1;
  if (coursePosition < 1) {
    return null;
  }

  const copyArgs = {
    courseName: course.name,
    s12nName: getS12nDisplayName(s12n.name, s12n.productVariant),
    coursePosition,
    courseCount: s12n.courseIds.length,
  };

  // The optimize-mix-and-match experiment (MONETIZE-3313) uses the longer phrasing from the updated design
  if (variant === 'experiment') {
    return _t(
      '#{courseName} is part of the larger #{s12nName} (Course #{coursePosition} of #{courseCount}).',
      copyArgs
    );
  }

  return _t('#{courseName} is part of the #{s12nName} and is Course #{coursePosition} of #{courseCount}.', copyArgs);
};

/**
 * Renders the specialization context copy as a text node so it can be dropped into an existing
 * typography wrapper. Renders nothing when the copy is unavailable.
 */
const MixAndMatchS12nContextMessage = ({ variant }: { variant?: 'default' | 'experiment' }) => {
  const copy = useMixAndMatchS12nContextCopy(variant);

  if (!copy) {
    return null;
  }

  return <React.Fragment>{copy}</React.Fragment>;
};

export default MixAndMatchS12nContextMessage;
